// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

import { useUser } from '@/app/user-context';
import {
  getActiveClaimSessionSync,
  loadClaimSession,
  subscribeClaimSession,
  type ActiveClaimSession,
} from '@/services/claimSession';

import { useInviteViewer } from '@/hooks/useInviteViewer';

export type GuestSignup = {
  /** Show the "create account" affordance: this visitor arrived by invite and has no account. */
  canCreateAccount: boolean;
  /** They hold a claim token, so registering adopts their player rather than minting a new one. */
  hasClaimedPlayer: boolean;
};

/**
 * The pure half of {@link useGuestSignup}, exported for tests (mirrors resolveInviteViewer).
 *
 * Two different questions, deliberately: a claim session at all is enough to be *offered* an
 * account, but only a claim token backs the promise that their scores come with them — that token
 * is what /register presents to adopt the player. A session can outlive its token, or arrive
 * without one when the claim was scoped to a different round.
 *
 * Presence of the token is the test, not usability: it expires after 15 minutes while the claim
 * lock never does, and the BFF validates an expired-but-signed one for adoption. Checking
 * hasClaimAccess() here would retract the promise from exactly the guest it exists for. Same
 * reading the register screen itself applies.
 */
export const resolveGuestSignup = (
  userToken: string | null | undefined,
  claimSession: ActiveClaimSession | null,
  isInviteViewer: boolean,
): GuestSignup => ({
  hasClaimedPlayer: !userToken && !!claimSession?.claimAccessToken,
  canCreateAccount: !userToken && (!!claimSession?.claimClientId || isInviteViewer),
});

/**
 * "Could this visitor become a registered user?" — true for the two audiences that reach the app
 * without an account: a guest who claimed a player from an invite link, and a spectator watching
 * read-only. Both convert through /register, which adopts the claimed player when there is one.
 *
 * The claim session lives in AsyncStorage, so it arrives a tick late on a cold start; subscribing
 * keeps the affordance in step when a claim is made or released while the screen is open.
 */
export const useGuestSignup = (): GuestSignup => {
  const { user } = useUser();
  const { isInviteViewer } = useInviteViewer();
  const [claimSession, setClaimSession] = useState<ActiveClaimSession | null>(() =>
    getActiveClaimSessionSync()
  );

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .then(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
        }
      })
      .catch(() => undefined);
    const unsubscribe = subscribeClaimSession((next) => {
      if (!cancelled) {
        setClaimSession(next);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return resolveGuestSignup(user?.token, claimSession, isInviteViewer);
};

export default useGuestSignup;
